import { isEmail } from 'class-validator';
import {
  addDays,
  differenceInDays,
  endOfDay,
  isAfter,
  isBefore,
  isWithinInterval,
  startOfDay,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import moment from 'moment';
import { QueryRunner } from 'typeorm';
import {
  DATE_FORMATS,
  FAILURE_MESSAGE,
  IMPORT_GUESTS_PLATFORMS,
  LOYALTY_STEPS,
  OFF_PREM_SOURCES,
  ON_PREM_SOURCES,
  US_FORMAT,
} from '../constant/constant';
import { cloudFrontUrls, StockStatus } from '../constant/enum';
import { BrandbookFont, GroupedBrandBookContent } from '../types/brandBook/content.type';
import { BrandbookSocials, DataFor } from '../types/brandBook/enum.types';
import { CampaignScheduleOptions, CampaignStatus } from '../types/campaign/campaign.types';
import { InvoiceStatus } from '../types/catering/cateringInvoice.types';
import { ICateringOrder, ICtrOrder } from '../types/catering/cateringOrder.types';
import { ICateringOrderServiceCharge, ICateringOrderTax } from '../types/catering/cateringOrderTax.enum';
import { HourType, IDealDataResponse, ItemsType } from '../types/deal/deal.types';
import { DateRange } from '../types/inventory/invoice/invoice.types';
import { Schedules, SelectedMenus, StellarPlaylist } from '../types/menu-board/playlist.types';
import { RestaurantTiming as ResTiming, Timing, TvPlaylist } from '../types/menu-board/Screen.types';
import { ChannelVisibilityType, RecordStatus } from '../types/menu/enums.types';
import { Item, ModifierOptions } from '../types/menu/menu.types';
import { IRestaurant, RestaurantMode } from '../types/restaurant/restaurant.types';
import { dayMap } from '../types/restaurant/restaurantTiming.types';
import { ReviewDurations } from '../types/reviewManagement/reviewManagement.type';
import { OrderSource } from '../types/ticket/ticket.types';
import { TimeLogType } from '../types/timelog/timelog.types';
import { AnalyticsDateRangeENUM } from '../types/websiteAnalytics/analytics.types';
import { DateRangeString } from '../types/reports/dashboardReports.types';
import { DateRangeFilter } from '../types/overview/overview.types';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ClassifiedCustomers, CustomerIssue } from '../types/guest/guests.types';
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export function calculateAmountFromPercentage(amount: number, percentage: number) {
  return Number(((amount * percentage) / 100).toFixed(2));
}

export function timeDifference(startTime: Date | string, endTime: Date | string) {
  const start = startTime instanceof Date ? startTime : new Date(startTime);
  const end = endTime instanceof Date ? endTime : new Date(endTime);
  return ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toFixed(2);
}

export const dateDifference = (start: Date | null, end: Date | null): number | null => {
  if (!start || !end) return 0;
  const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endUTC - startUTC) / (1000 * 60 * 60 * 24)) + 1;
};

export const toMinuteMs = (d: Date) => Math.floor(d.getTime() / 60000) * 60000;

export const getTotalHours = (partialDays: any[]) => {
  return (partialDays ?? [])
    .reduce((sum, day) => {
      if (day?.startTime && day?.endTime) {
        const start = day.startTime instanceof Date ? day.startTime : new Date(day.startTime);
        const end = day.endTime instanceof Date ? day.endTime : new Date(day.endTime);
        return sum + (toMinuteMs(end) - toMinuteMs(start)) / (1000 * 60 * 60);
      }
      return sum;
    }, 0)
    .toFixed(2);
};

export function findShiftsForEntry(
  currentClockInTime: Date,
  currentClockOutTime: Date,
  payRollStartTime: Date,
  payRollEndTime: Date
): Date {
  let shiftStart = new Date(payRollStartTime);
  let shiftStartClockIn = new Date();
  while (isBefore(shiftStart, payRollEndTime)) {
    const shiftEnd = new Date(shiftStart);
    shiftEnd.setHours(shiftStart.getHours() + 24); // Assuming shifts are 24 hours long

    // If the user has only clocked in and there's no valid clock-out time yet,
    // we skip clock-out validation (assume it's in progress or not required).
    // Otherwise, ensure clock-out is either within the shift window
    // or within a 3-minute grace period after shift end.
    const isClockOutValid = isValidDate(currentClockOutTime)
      ? isWithinInterval(currentClockOutTime, { start: shiftStart, end: shiftEnd }) ||
        Math.abs(currentClockOutTime.getTime() - shiftEnd.getTime()) <= 180000 // 3-minute margin
      : true;
    // Check if the entry overlaps with this shift
    const isInInterval = isWithinInterval(currentClockInTime, { start: shiftStart, end: shiftEnd }) && isClockOutValid; // Allow 3 min margin
    if (isInInterval) {
      shiftStartClockIn = shiftStart;
      break;
    }
    // Move to the next shift
    shiftStart = new Date(shiftEnd);
  }
  return shiftStartClockIn; // Return the list of shifts or null if no shift found
}

export function isEditWithinShifts(
  updatedClockInTime: Date,
  updatedClockOutTime: Date,
  currentClockInTime: Date,
  currentClockOutTime: Date,
  payRollStartTime: Date,
  payRollEndTime: Date
): boolean {
  // Find the start time of the shift for the current entry

  const shiftStartDateTime = findShiftsForEntry(
    currentClockInTime,
    currentClockOutTime,
    payRollStartTime,
    payRollEndTime
  );
  if (!shiftStartDateTime) return false; // No shifts found for the entry
  // Calculate the end time of the shift by adding 24 hours to the start time

  const shiftEnd = new Date(shiftStartDateTime);
  // Calculate shift end time by adding 24 hours and 59 seconds to the shift start time.
  // This is useful when defining a full-day shift duration with a slight buffer at the end.
  // We use `getTime()` and `setTime()` to work with exact milliseconds for precision.
  // 24 hours = 24 * 60 * 60 * 1000 milliseconds
  // 59 seconds = 59 * 1000 milliseconds
  // Total added time = 24 hours + 59 seconds
  shiftEnd.setTime(shiftStartDateTime.getTime() + 24 * 60 * 60 * 1000 + 59 * 1000);
  /*
   * This condition checks if both `updatedClockOutTime` and `currentClockOutTime` are invalid dates.
   * If true, it indicates that the user has only clocked in so far, and someone is attempting
   * to update the clock-in entry, so we will not validate the clock-out time.
   */

  if (!isValidDate(updatedClockOutTime) && !isValidDate(currentClockOutTime)) {
    const isInInterval: boolean = isWithinInterval(updatedClockInTime, { start: shiftStartDateTime, end: shiftEnd });
    return isInInterval;
  }

  // Check if the edit clock-in and clock-out times fall within the shifts
  const isInInterval: boolean =
    isWithinInterval(updatedClockInTime, { start: shiftStartDateTime, end: shiftEnd }) &&
    isWithinInterval(updatedClockOutTime, { start: shiftStartDateTime, end: shiftEnd });
  return isInInterval;
}

export function editTimeLogBreakVoliation(
  updatedClockInTime: Date | null,
  updatedClockOutTime: Date | null,
  item?: TimeLogType
): string | undefined {
  // Extract break details from the item if available, otherwise default to an empty array
  const breaksDetails = item?.breakInfos ?? [];

  // Check if there are any breaks
  if (breaksDetails.length > 0) {
    // Get the first and last breaks from the list
    const firstBreak = breaksDetails[breaksDetails.length - 1];
    const lastBreak = breaksDetails[0];

    // Check if the updated clock-in time is after the start time of the last break
    const clockInCheck = updatedClockInTime && isAfter(updatedClockInTime, new Date(firstBreak.startTime ?? ''));

    // Check if the updated clock-out time is before the end time of the first break
    const clockOutCheck =
      updatedClockOutTime && isValidDate(updatedClockOutTime)
        ? isBefore(updatedClockOutTime, new Date(lastBreak?.endTime ?? ''))
        : false;

    if (clockInCheck) {
      return FAILURE_MESSAGE.TIME_LOG.CLOCK_IN_VOLIATION;
    }
    if (clockOutCheck) {
      return FAILURE_MESSAGE.TIME_LOG.CLOCK_OUT_VOLIATION;
    }
  }
  // If no overlap is detected, return undefined
  return undefined;
}

export const decimalRuleConvert = (input: number | undefined | null) => {
  return Number((input ?? 0).toFixed(2));
};

export const checkIsSocialLogin = (username: string) => {
  return username.toString().includes('google') || username.toString().includes('facebook');
};

export const roundToSignificantDigits = (value: number, significantDigits = 2) => {
  if (value === 0) return 0;
  if (value > 1) {
    return Math.round(value * Math.pow(10, significantDigits)) / Math.pow(10, significantDigits);
  }

  const magnitude = Math.floor(Math.log10(Math.abs(value))) + 1;

  const decimalPlaces = Math.max(0, significantDigits - magnitude);

  const factor = Math.pow(10, decimalPlaces);
  return Math.round(value * factor) / factor;
};

export const formatNumericString = (input: string, decimalPlaces = 2, allowNegative = false) => {
  const decimalLimitRegex = new RegExp(`(\\.\\d{0,${decimalPlaces}})\\d*$`); // Dynamically create regex for decimal limit

  return input
    .replace(
      new RegExp(`[^0-9.${allowNegative ? '-' : ''}]|(?<!^)-|(?<=\\..*)\\.|^0+(?=\\d)`, 'g'), // Handle invalid chars, misplaced negatives, extra decimals, and leading zeros
      ''
    )
    .replace(decimalLimitRegex, '$1'); // Limit to specified decimal places
};

// Generic function to format a number to fixed-point notation
export const formatNumber = (value: any, fractionDigits = 2): number | null => {
  if (value == null || isNaN(Number(value))) {
    return null;
  }
  return Number(Number(value).toFixed(fractionDigits));
};

export const formatTwoDigitNumber = (value: number | string): string => {
  return String(value).padStart(2, '0');
};

export const calculatePercentage = (numerator: any, denominator: any): number | null => {
  // Coerce to number — pg driver returns Postgres numeric/SUM/ROUND as strings (e.g. "1234.56")
  numerator = numerator == null ? 0 : Number(numerator);
  denominator = denominator == null ? 0 : Number(denominator);

  // After coercion, reject values that are still not valid numbers (e.g. non-numeric strings)
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    throw new Error('Both numerator and denominator must be numbers.');
  }

  // Handle case where denominator is zero
  if (denominator === 0) {
    return null;
  }

  // Calculate the percentage
  return Number(Number((numerator / denominator) * 100).toFixed(2));
};

export function isValidEmail(email: string) {
  return isEmail(email);
}

export function isValidTime(value: string) {
  const timePattern = /^(\d{1,2}|\d{2}):[0-5]\d:[0-5]\d$/; // 24-hour format
  return timePattern.test(value);
}

// export function verifyWebhookSignature(
//   body: Buffer,
//   signature: string,
//   secret: string,
//   encoding: BufferEncoding = 'hex',
//   algorithm = 'sha256'
// ): boolean {
//   const sig = Buffer.from(signature, encoding);
//   const hmac = crypto.createHmac(algorithm, secret);
//   const digest = hmac.update(body).digest();

//   return sig.length === digest.length && crypto.timingSafeEqual(digest, sig);
// }

export function loyaltyItemName(item: Item, modifierOption?: ModifierOptions | null) {
  return `${item.itemName}` + (modifierOption ? ` - ${modifierOption?.optionName}` : '');
}

interface LoyaltyItemPrice extends Item {
  totalPrice?: number | null;
  calculatePricesWithTaxIncluded?: boolean | null;
}

export function loyaltyItemPrice(item: LoyaltyItemPrice, modifierOption?: ModifierOptions | null) {
  const itemPrice = item.calculatePricesWithTaxIncluded ? item.totalPrice : item.itemPrice;
  const modifierOptionPrice = item.calculatePricesWithTaxIncluded ? modifierOption?.totalPrice : modifierOption?.price;
  if (itemPrice && itemPrice > 0) return itemPrice;
  if (modifierOptionPrice && modifierOptionPrice > 0) return modifierOptionPrice;
  return 0;
}

export const createS3FolderPathForInventory = (
  businessId: number | undefined,
  restaurantId: number | undefined,
  folderName: string
) => {
  return `dashboard/tenant_${businessId}/location_${restaurantId}/inventory/${folderName}`;
};

export function getTimezoneOffsetString(date: Date) {
  const offset = date.getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
}

/**
 * Bulk inserts using raw qquery
 *
 * @async
 * @param {string[]} queryRunner - Query Runner.
 * @param {string[]} table - Name of table in database.
 * @param {string[]} data - Array of data that will be inserted. Name of keys need to be same as in database.
 * @returns {Promise<any[]>} - Returns the inserted data in database form
 * @throws {Error} - If an error occurs with running raw queries.
 */
export async function bulkInsert(queryRunner: QueryRunner, table: string, data: any[]) {
  if (data.length === 0) return [];
  const fields = Object.keys(data[0]);
  const items = data.map((item) => Object.values(item));

  let fieldCounter = 1;
  const fieldsQuery = fields.map((item) => `"${item}"`).join(', ');
  const fieldsIndexes = () => fields.map((_i) => `$${fieldCounter++}`).join(', ');

  const result = await queryRunner.query(
    `
    INSERT INTO ${table} (${fieldsQuery})
    VALUES ${items.map((_i) => `(${fieldsIndexes()})`).join(', ')}
    RETURNING *
  `,
    [...items.flat()]
  );
  return result;
}

interface BulkUpdateInput<T> {
  tableName: string;
  primaryKey: keyof T;
  updateColumn: keyof T;
  data: Pick<T, keyof T>[];
  queryRunner: any; // TypeORM QueryRunner
}

export async function bulkUpdate<T>({
  tableName,
  primaryKey,
  updateColumn,
  data,
  queryRunner,
}: BulkUpdateInput<T>): Promise<void> {
  if (!data.length) return;

  const ids = data.map((item) => item[primaryKey]);
  const caseStatements = data
    .map((item) => {
      const type = typeof item[updateColumn];
      let mappings: { [key in string]: Pick<T, keyof T>[keyof T] | string } = {
        object: `'${JSON.stringify(item[updateColumn]).replace(/'/g, "''")}'::jsonb`,
        number: item[updateColumn],
      };
      const forbiddenType = Object.keys(mappings).includes(type);
      if (!forbiddenType) {
        mappings = {
          ...mappings,
          [type]: `'${`${item[updateColumn]}`.replace(/'/g, "''")}'`,
        };
      }
      const value = mappings[type];
      return `WHEN ${item[primaryKey]} THEN ${value}`;
    })
    .join('\n');

  const query = `
    UPDATE ${tableName}
    SET "${updateColumn as string}" = CASE ${primaryKey as string}
      ${caseStatements}
    END
    WHERE ${primaryKey as string} IN (${ids.join(', ')});
  `;

  await queryRunner.query(query);
}

export function getEmailUrls(restaurant?: IRestaurant | null) {
  const domainSettings = restaurant?.domainSettings;
  const domain = domainSettings?.domainName ? domainSettings.domainName : domainSettings?.cloudFrontDomainName || '';
  const url = domain ? createUrl(domain, domainSettings?.isCertificateValid || false) : '';

  const data = restaurant?.brandBook?.brandBookContent ?? [];
  const groupedData = groupBrandbookContent(data);
  const socials = getSocialsDataFromBrandbook(groupedData);
  const logos = getLogosDataFromBrandbook(groupedData);

  const findSocial = (key: string) =>
    socials.find((item: { name: string }) => item.name.toLowerCase() === key.toLowerCase())?.value;

  const facebook = findSocial(BrandbookSocials.FACEBOOK);
  const instagram = findSocial(BrandbookSocials.INSTAGRAM);
  const twitter = findSocial(BrandbookSocials.TWITTER);
  const yelp = findSocial(BrandbookSocials.YELP);
  const google = findSocial(BrandbookSocials.GOOGLE);
  const tiktok = findSocial(BrandbookSocials.TIKTOK);
  const youtube = findSocial(BrandbookSocials.YOUTUBE);

  const logo = logos[0]?.fileUrl;

  return {
    domain: url,
    facebook,
    instagram,
    twitter,
    yelp,
    logo,
    google,
    tiktok,
    youtube,
  };
}

export function createUrl(domain: string, isCertificateValid: boolean): string {
  const protocol = isCertificateValid ? 'https' : 'http';
  return `${protocol}://${domain ? domain : 'yourdomain.com'}`;
}

export const groupBrandbookContent = (data: any): GroupedBrandBookContent => {
  let updatedAt = '';
  if (data.length > 0) {
    const groupedData = data.reduce((acc: any, item: any, index: any) => {
      const key = item.dataFor;
      if (item.updatedAt) {
        updatedAt = item.updatedAt;
      }
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
    Object.keys(groupedData).forEach((key) => {
      groupedData[key].sort((a: any, b: any) => a.id - b.id);
    });

    if (updatedAt) {
      groupedData['updatedAt'] = updatedAt;
    }

    return groupedData;
  } else {
    return {};
  }
};

export const getFontsDataFromBrandbook = (groupedData?: GroupedBrandBookContent): BrandbookFont[] => {
  return (groupedData?.[DataFor.FONT] ?? [])?.map((fontData: any) => ({
    id: fontData.id,
    name: fontData.name,
    fontFamily: fontData.content?.fontFamily ?? '',
    fontStyle: fontData.content?.fontStyle,
    fontSize: fontData.content?.fontSize,
    fontWeight: fontData.content?.fontWeight,
    fontUrl: fontData.content?.fontUrl,
    isCustom: fontData.content?.isCustom,
    isEditable: fontData.content?.isEditable,
    isDefault: fontData?.isDefault,
  }));
};

export const getSocialsDataFromBrandbook = (groupedData?: GroupedBrandBookContent) => {
  return (groupedData?.[DataFor.SOCIAL] ?? [])?.map((socialData: any) => {
    return {
      id: socialData.id,
      name: socialData.name,
      value: socialData.content.value,
    };
  });
};

export const getLogosDataFromBrandbook = (groupedData?: GroupedBrandBookContent) => {
  return (groupedData?.[DataFor.LOGO] ?? [])?.map((logoData: any) => {
    return {
      id: logoData.id,
      fileStoreId: logoData.fileStoreId,
      name: logoData.name,
      fileUrl: logoData.fileStore?.fileUrl,
      size: logoData.fileStore?.size,
      type: logoData.fileStore?.type,
      fileFor: logoData.fileStore?.fileFor,
    };
  });
};

export const getPreviousYear = (distance = 1) => moment().subtract(distance, 'y').format('yyyy');

// create chunks of array into passed set lenght
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  return array.reduce((result, item, index) => {
    const chunkIndex = Math.floor(index / chunkSize);
    if (!result[chunkIndex]) {
      result[chunkIndex] = [];
    }
    result[chunkIndex].push(item);
    return result;
  }, [] as T[][]);
}

export function getAnalyticsDateRange(rangeType: AnalyticsDateRangeENUM) {
  const today = new Date();
  const end = endOfDay(today);

  switch (rangeType) {
    case AnalyticsDateRangeENUM.YESTERDAY: {
      const yesterday = subDays(today, 1);
      return {
        start: startOfDay(yesterday),
        end: endOfDay(yesterday),
      };
    }

    case AnalyticsDateRangeENUM.LAST_7_DAYS: {
      return {
        start: startOfDay(subDays(today, 7)),
        end,
      };
    }

    case AnalyticsDateRangeENUM.LAST_14_DAYS: {
      return {
        start: startOfDay(subDays(today, 14)),
        end,
      };
    }

    case AnalyticsDateRangeENUM.LAST_30_DAYS: {
      return {
        start: startOfDay(subDays(today, 30)),
        end,
      };
    }

    case AnalyticsDateRangeENUM.LAST_90_DAYS: {
      return {
        start: startOfDay(subDays(today, 90)),
        end,
      };
    }

    case AnalyticsDateRangeENUM.LAST_6_MONTHS: {
      return {
        start: startOfDay(subMonths(today, 6)),
        end,
      };
    }

    case AnalyticsDateRangeENUM.LAST_1_YEAR: {
      return {
        start: startOfDay(subYears(today, 1)),
        end,
      };
    }

    default:
      throw new Error('Invalid date range type');
  }
}

export function getPreviousAnalyticsDateRange(rangeType: AnalyticsDateRangeENUM): { start: Date; end: Date } {
  const { start, end } = getAnalyticsDateRange(rangeType);
  const duration = end.getTime() - start.getTime();

  const previousEnd = startOfDay(subDays(start, 1));
  const previousStart = startOfDay(new Date(previousEnd.getTime() - duration));

  return { start: previousStart, end: endOfDay(previousEnd) };
}

export function getRangeReadableString(selectedRange: AnalyticsDateRangeENUM): string {
  switch (selectedRange) {
    case AnalyticsDateRangeENUM.YESTERDAY:
      return '1 day';

    case AnalyticsDateRangeENUM.LAST_7_DAYS:
      return '7 days';

    case AnalyticsDateRangeENUM.LAST_14_DAYS:
      return '14 days';

    case AnalyticsDateRangeENUM.LAST_30_DAYS:
      return '1 month';

    case AnalyticsDateRangeENUM.LAST_90_DAYS:
      return '3 months';

    case AnalyticsDateRangeENUM.LAST_6_MONTHS:
      return '6 months';

    case AnalyticsDateRangeENUM.LAST_1_YEAR:
      return '1 year';

    default:
      throw new Error('Invalid selected range');
  }
}

export function formatDateToString(value: Date | string): string {
  return new Date(value).toISOString().split('T')[0]; // Returns YYYY-MM-DD
}

export function formatTimeToString(value: Date | string): string {
  return new Date(value).toISOString().split('T')[1].slice(0, 8); // Returns HH:mm:ss
}
export const capitalFirst = (str: string) =>
  str.toLowerCase().replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

export const checkCampaignExpiry = (campaign: any) => {
  if (campaign.status === CampaignStatus.EXPIRED) return campaign.status;
  const expireStatuses = [CampaignStatus.NEEDS_EDIT, CampaignStatus.REVIEW];
  const isRightAway = campaign.scheduleOption === CampaignScheduleOptions.RIGHT_AWAY;
  const currentDate = dayjs();
  const scheduleDate = dayjs(campaign.schedule).set('second', 0);
  const endDate = dayjs(campaign.endDate).set('second', 0);
  const isAfter = currentDate.isAfter(isRightAway ? endDate : scheduleDate);
  const isExpired = isAfter && expireStatuses.includes(campaign.status);
  campaign.status = isExpired ? CampaignStatus.EXPIRED : campaign.status;
  return campaign.status;
};

export const getLoginRedisCacheKey = (appId: number, userId: string) => {
  if (!appId || !userId) return '';
  return `appId_${appId}-getAbilityForUser-${userId}`;
};

export const getModifiersObject = (modifiers?: { name: string; price?: number }[]) => {
  if (!modifiers) return {};

  const result = modifiers?.reduce((acc, modifier) => {
    if (modifier.price) {
      acc[modifier?.name] = modifier.price;
    }
    return acc;
  }, {} as Record<string, number>);

  return result;
};

export const toSeconds = (duration: string): number => {
  const parts = duration.split(':').map(Number);
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
};

// Function to convert total seconds back to "HH:MM:SS"
export const toHMS = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// Calculate the total duration in seconds
export const getTotalDuration = (durations: string[]): string => {
  const totalSeconds = durations.reduce((sum, duration) => sum + toSeconds(duration), 0);
  return toHMS(totalSeconds);
};

export const getPlaylistType = (type: string) => {
  if (type.includes('image')) {
    return 'Image';
  }

  if (type.includes('video')) {
    return 'Video';
  }
};

export function parseDuration(duration: string): number {
  if (!duration) return 0;
  const [hours, minutes, seconds] = duration.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

export const formatRestaurantTimingDay = (dayName: string) => {
  const dayMap: Record<string, string> = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };

  return dayMap[dayName];
};

export const sortPlaylistItems = (playlist: StellarPlaylist) => {
  return playlist.items.sort((a, b) => a.sortOrder - b.sortOrder);
};

export const removeNullScheduleData = (playlists: StellarPlaylist[]) => {
  if (!playlists) return playlists;

  const result = playlists
    .filter((playlist) => playlist.schedules) // Remove playlists with null schedules
    .map((playlist) => ({
      ...playlist,
      items: playlist.items.filter((item) => {
        if (item.playlistType !== 'Template') return true;

        if (item.template?.content) {
          item.template.content = removeTemplateContent(item.template.content);
        }

        return Array.isArray(item.template?.content) && item.template?.content?.length > 0; // Return the item if it still has valid content
      }),
    }))
    .filter((playlist) => playlist.items.length > 0); // Remove playlists with no items left

  return result;
};

export const removeTemplateContent = (content: SelectedMenus[]) => {
  return content
    .filter((menu) => menu.schedules) // Remove menus with null schedules
    .map((menu) => ({
      ...menu,
      categories: menu.categories
        .filter((category) => category.schedules) // Remove categories with null schedules
        .map((category) => ({
          ...category,
          items: category?.items?.filter((item) => item.schedules), // Remove items with null schedules
        }))
        .filter((category) => category?.items && category?.items?.length > 0), // Remove categories with no items left
    }))
    .filter((menu) => menu.categories.length > 0); // Remove menus with no categories left
};

export const getActiveDaySchedules = (
  setting: any,
  type: 'Item' | 'Menu' | 'Category' | 'Tv',
  timings?: ResTiming[] | null
) => {
  if (setting?.status === RecordStatus.ACTIVE && Array.isArray(setting?.visibilitySettings) && setting.type === type) {
    const visibilitySetting = setting?.visibilitySettings?.find(
      (vs: any) =>
        (vs.channelVisibilityType === ChannelVisibilityType.OnPrem ||
          vs.channelVisibilityType === ChannelVisibilityType.Custom) &&
        vs.status === RecordStatus.ACTIVE
    );

    if (visibilitySetting?.isDefault) {
      return timings;
    }

    if (visibilitySetting && Array.isArray(visibilitySetting?.daySchedules)) {
      const activeDaySchedules = visibilitySetting?.daySchedules
        ?.filter((daySchedule: any) => daySchedule.status === RecordStatus.ACTIVE)
        .map((day: any) => {
          if (day.availabilityType === 'AllDay') {
            return {
              day: day.day,
              startTime: '00:00:00',
              endTime: '23:59:59',
            };
          } else {
            return { day: day.day, startTime: day.startTime, endTime: day.endTime };
          }
        });

      return activeDaySchedules.length > 0 ? activeDaySchedules : null;
    }
  }

  return null;
};

export const getPlaylistData = (query: any, timings?: ResTiming[] | null) => {
  const data = {
    playlists: query.map((tv: any) => {
      return {
        id: tv.playlist.id,
        name: tv.playlist.name,
        sortOrder: tv.sortOrder,
        schedules: getActiveDaySchedules(tv?.setting, 'Tv', timings),
        items:
          tv.playlist?.items.length > 0
            ? tv.playlist.items.map((item: any) => ({
                id: item.id,
                name: item.name,
                duration: item.duration,
                imageUrl: item.imageUrl,
                sortOrder: item.sortOrder,
                type: item.type,
                playlistType: !item.template ? getPlaylistType(item.type) : 'Template',
                template: item.template
                  ? {
                      ...item.template,
                      content: item.templateMenus.map((menu: any) => {
                        return {
                          id: menu.menu.id,
                          name: menu.menu.menuName,
                          schedules: getActiveDaySchedules(menu?.menu?.setting, 'Menu', timings),
                          categories: menu?.templateCategories?.map((category: any) => ({
                            id: category?.category?.id,
                            name: category?.category?.categoryName,
                            schedules: getActiveDaySchedules(category?.category?.setting, 'Category', timings),
                            items: category.templateItems?.map((item: any) => ({
                              id: item?.item?.id,
                              itemName: item.item.itemName,
                              itemPrice: item.item.itemPrice,
                              itemDescription: item.item.itemDescription,
                              itemPicture: item.item.itemPicture,
                              modifiers: item?.item?.modifiers
                                ? getModifiersObject(
                                    item.item.modifiers
                                      .filter((modifier: any) => modifier.isSizeModifier === true)
                                      .flatMap((modifier: any) =>
                                        modifier.modifierModifierOptions.map((modifierOpt: any) => ({
                                          name: modifierOpt.modifierOption.optionName,
                                          price: modifierOpt.modifierOption.price,
                                        }))
                                      )
                                  )
                                : null,
                              outOfStock: item.item.stockStatus === StockStatus.OutOfStock,
                              schedules: getActiveDaySchedules(item?.item?.setting, 'Item', timings),
                            })),
                          })),
                        };
                      }),
                    }
                  : null,
              }))
            : [],
      };
    }),
  };

  return data;
};

export const formatRestaurantTiming = (timings?: Timing[] | null) => {
  return Array.isArray(timings)
    ? timings?.map((timing) => ({
        day: timing.day,
        startTime: timing.startTime,
        endTime: String(timing.endTime),
      }))
    : null;
};

export const getNameBreakDownFromFullName = (name: string): { firstName: string; lastName: string } => {
  const splitName = name.split(' ').filter((e) => e);
  const lastName = splitName.length > 1 ? splitName.pop() ?? '' : '';
  const firstName = splitName.length > 1 ? splitName.join(' ') : splitName[0] ?? '';
  return { firstName, lastName };
};

export function getFullDayName(shortDay: string): string {
  return dayMap[shortDay] || shortDay;
}
export function replaceS3WithCloudFront(url: string, env: string, bucket?: string): string {
  const s3Url = `${bucket}.s3.us-west-2.amazonaws.com`;
  return cloudFrontUrls[env] ? url.replace(s3Url, cloudFrontUrls[env]) : url;
}
/**
 * Checks if the given value is a valid Date object.
 * A valid Date object is an instance of Date and its time value is not NaN.
 *
 * @param date - The value to check
 * @returns {boolean} - Returns true if the value is a valid Date, otherwise false.
 *
 * @example
 * isValidDate(new Date());             // true
 * isValidDate(new Date('invalid'));    // false
 * isValidDate('2023-01-01');           // false
 * isValidDate(null);                   // false
 */
export const isValidDate = (date: any): boolean => {
  if (date === null || date === undefined) return false;
  const parsed = date instanceof Date ? date : new Date(date);
  return parsed instanceof Date && !isNaN(parsed.getTime());
};

export function getReviewDurations(duration?: ReviewDurations) {
  const lastDayOfPreviousMonth = dayjs().subtract(1, 'month').endOf('month').toDate();
  const durations = {
    [ReviewDurations.ALL_TIME]: {
      from: dayjs().toDate(),
      to: dayjs().toDate(),
    },
    [ReviewDurations.MONTH]: {
      from: dayjs(lastDayOfPreviousMonth).startOf('month').toDate(),
      to: lastDayOfPreviousMonth,
    },
    [ReviewDurations.HALF_YEAR]: {
      from: dayjs(lastDayOfPreviousMonth).subtract(5, 'months').startOf('month').toDate(),
      to: lastDayOfPreviousMonth,
    },
    [ReviewDurations.YEAR]: {
      from: dayjs(lastDayOfPreviousMonth).subtract(11, 'months').startOf('month').toDate(),
      to: lastDayOfPreviousMonth,
    },
  };
  return duration ? durations[duration] : durations[ReviewDurations.MONTH];
}

export function findMissingLoyaltySteps(arr: number[], current?: number): number[] {
  const step = [LOYALTY_STEPS];

  if (arr.length === 0) return step;

  const missing: number[] = arr.includes(LOYALTY_STEPS) ? [] : step;
  const sorted = [...arr].sort((a, b) => a - b);
  const start = sorted[0];
  const end = sorted[sorted.length - 1];

  for (let i = start; i <= end; i += LOYALTY_STEPS) {
    if (!sorted.includes(i)) {
      missing.push(i);
    }
  }
  if (current) missing.push(current);

  return missing.sort((a, b) => a - b);
}

interface ObjProps {
  getItems: { value: number; label: string }[];
  buyItems: { value: number; label: string }[];
  orderSources: string[];
  startDate: dayjs.Dayjs | undefined;
  endDate: dayjs.Dayjs | undefined;
  startTime: dayjs.Dayjs | undefined;
  endTime: dayjs.Dayjs | undefined;
  days: string[];
  hourType?: string;
  isEntire?: boolean;
}

const getOrderSources = (orderSources: string[]) => {
  const dealOffPrem = orderSources?.some((item) => OFF_PREM_SOURCES.includes(item as OrderSource));
  const dealOnPrem = orderSources.some((item) => ON_PREM_SOURCES.includes(item as OrderSource));

  if (dealOffPrem && dealOnPrem) {
    return 'both dine-in and takeout orders';
  }

  if (dealOffPrem) {
    return 'takeout orders';
  }

  if (dealOnPrem) {
    return 'dine-in orders';
  }
};

const getWeekDays = (days: string[]) => {
  if (days.length === 2 && days.includes('Sat') && days.includes('Sun')) {
    return 'Valid only on Weekends (Saturday & Sunday)';
  }

  if (days.length === 7) {
    return 'Valid all week';
  }

  return `Valid on ${days.join(', ')}`;
};

export const predefinedTerms = (obj: ObjProps) => {
  const startTime = obj.startTime?.format('hh A');
  const endTime = obj.endTime?.format('hh A');
  const term3 =
    obj.hourType === HourType.ALL_DAY
      ? `${getWeekDays(obj.days)} All day`
      : `${getWeekDays(obj.days)} ${startTime} to ${endTime}`;
  const term4 = `Available for ${getOrderSources(obj.orderSources)}`;
  const terms = [term3, term4];

  const buyTerms = [];
  const getTerms = [];

  if (obj.buyItems.length === 1) {
    buyTerms.push(`First ${obj.buyItems && obj.buyItems[0].label} must be purchased at full price`);
  } else if (obj.buyItems.length > 1) {
    buyTerms.push(`${(obj.buyItems ?? []).map((item) => item.label).join(', ')} must be purchased at full price`);
  }

  if (obj.getItems.length === 1) {
    getTerms.push(`Discount applies to ${obj.getItems && obj.getItems[0].label} only`);
  } else if (obj.isEntire) {
    getTerms.push(`Discount applies to entire check`);
  } else {
    getTerms.push(`Discount applies to ${(obj.getItems ?? []).map((item) => item.label).join(', ')}`);
  }
  return [...buyTerms, ...getTerms, ...terms];
};

export function getHoursDifference(date1: string, date2: string): number {
  if (!date1) return 0;
  const startDate = new Date(date1);
  const endDate = new Date(date2);

  const diffInMs = Math.abs(endDate.getTime() - startDate.getTime());
  const diffInHours = diffInMs / (1000 * 60 * 60); // Convert milliseconds to hours

  return Math.round(diffInHours);
}

export const getDealHourType = (endTime: string, startTime: string) => {
  const timeDifference = getHoursDifference(endTime, startTime);
  const hourType = timeDifference === 24 ? HourType.ALL_DAY : HourType.SELECT_HOURS;
  return hourType;
};

export const getDealAppliedItems = (
  deal?: IDealDataResponse,
  itemsType?: ItemsType,
  buyOrGet: 'Buy' | 'Get' = 'Buy'
) => {
  if (!itemsType) return [];

  const fields = {
    [ItemsType.MENU]: {
      buyOrGet: `menu${buyOrGet}`,
      field: 'menuName',
    },
    [ItemsType.CATEOGRY]: {
      buyOrGet: `category${buyOrGet}`,
      field: 'categoryName',
    },
    [ItemsType.ITEM]: {
      buyOrGet: `item${buyOrGet}`,
      field: 'itemName',
    },
  };
  const values = fields[itemsType];
  const menus = deal?.menus ?? [];
  const categories = deal?.categories ?? [];
  const items = deal?.items ?? [];

  let result = [];

  switch (itemsType) {
    case ItemsType.MENU: {
      const buyOrGet = values.buyOrGet as 'menuBuy' | 'menuGet';
      const selectedField = values?.field as 'menuName';
      result = menus
        .filter((item) => !!item[buyOrGet])
        .map((item) => ({
          id: item[buyOrGet]?.id,
          label: item[buyOrGet]?.[selectedField],
        }));
      break;
    }
    case ItemsType.CATEOGRY: {
      const buyOrGet = values.buyOrGet as 'categoryBuy' | 'categoryGet';
      const selectedField = values?.field as 'categoryName';
      result = categories
        .filter((item) => !!item[buyOrGet])
        .map((item) => ({
          id: item[buyOrGet]?.id,
          label: item[buyOrGet]?.[selectedField],
        }));
      break;
    }
    case ItemsType.ITEM: {
      const buyOrGet = values.buyOrGet as 'itemBuy' | 'itemGet';
      const selectedField = values?.field as 'itemName';
      result = items
        .filter((item) => !!item[buyOrGet])
        .map((item) => ({
          id: item[buyOrGet]?.id,
          label: item[buyOrGet]?.[selectedField],
        }));
      break;
    }
  }

  result = result
    .map((item) => ({
      id: item.id ?? 0,
      label: item.label ?? '',
    }))
    .filter((item) => item.id !== 0 && item.label !== '');

  return result;
};

export const getTaxServiceChargeTotals =
  (itemsTotal: number) =>
  (item: Pick<ICateringOrderTax, 'type' | 'value'> | Pick<ICateringOrderServiceCharge, 'type' | 'value'>) => {
    const taxAmount = item.type === 'percentage' ? (itemsTotal * item.value) / 100 : item.value;
    return {
      ...item,
      amount: taxAmount,
    };
  };

export const getCateringOrderTotals = (order?: ICateringOrder) => {
  const itemsTotal = order?.items?.reduce((total, item) => total + item.unitPrice * item.quantity, 0) ?? 0;

  const serviceChargesTotal =
    order?.serviceCharges
      ?.map(getTaxServiceChargeTotals(itemsTotal))
      .reduce((total, charge) => total + charge.amount, 0) ?? 0;

  const itemsAndServiceChargeTotal = itemsTotal + serviceChargesTotal;

  const taxesTotal =
    order?.taxes
      ?.map(getTaxServiceChargeTotals(itemsAndServiceChargeTotal))
      .reduce((total, tax) => total + tax.amount, 0) ?? 0;

  const serviceChargeAndTaxTotal = taxesTotal + serviceChargesTotal;

  const total = Number((itemsAndServiceChargeTotal + taxesTotal).toFixed(2));

  const paidInvoices = order?.invoices?.filter((invoice) => invoice.invoiceStatus === InvoiceStatus.PAID);
  const pendingInvoice = order?.invoices?.find((invoice) => invoice.invoiceStatus === InvoiceStatus.PENDING);

  const paid = paidInvoices?.reduce((acc, inv) => acc + inv.paidAmount, 0) ?? 0;

  const due = Number((total - paid).toFixed(2));

  return {
    itemsTotal,
    taxesTotal,
    serviceChargesTotal,
    serviceChargeAndTaxTotal,
    total,
    paid,
    due,
    pendingInvoice,
  };
};

export const getCtrOrderTotals = (order?: ICtrOrder) => {
  const activeOrderItems =
    order?.ctrOrderItems?.filter((item: any) => !item?.status || item.status === RecordStatus.ACTIVE) ?? [];
  const itemsTotal = activeOrderItems.reduce((total, item) => total + item.unitPrice * item.quantity, 0);

  const serviceChargesTotal =
    order?.serviceCharges
      ?.map(getTaxServiceChargeTotals(itemsTotal))
      .reduce((total, charge) => total + charge.amount, 0) ?? 0;

  const itemsAndServiceChargeTotal = itemsTotal + serviceChargesTotal;

  const taxesTotal =
    order?.taxes
      ?.map(getTaxServiceChargeTotals(itemsAndServiceChargeTotal))
      .reduce((total, tax) => total + tax.amount, 0) ?? 0;

  const serviceChargeAndTaxTotal = taxesTotal + serviceChargesTotal;

  const total = Number((itemsAndServiceChargeTotal + taxesTotal).toFixed(2));

  const paidInvoices = order?.ctrInvoices?.filter((invoice) => invoice.invoiceStatus === InvoiceStatus.PAID);
  const pendingInvoice = order?.ctrInvoices?.find((invoice) => invoice.invoiceStatus === InvoiceStatus.PENDING);

  const paid = paidInvoices?.reduce((acc, inv) => acc + inv.paidAmount, 0) ?? 0;

  const due = Number((total - paid).toFixed(2));

  return {
    itemsTotal,
    taxesTotal,
    serviceChargesTotal,
    serviceChargeAndTaxTotal,
    total,
    paid,
    due,
    pendingInvoice,
  };
};

export const isNegativeNumber = (num: number) => !isNaN(num) && num < 0;

interface SortAndPaginateOptions<T> {
  data: T[];
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
  sortById?: string | number;
  nestedFields?: string[];
  dateFields?: string[];
  getNestedValue: (obj: any, path: string) => any;
}

export function sortAndPaginate<T>({
  data,
  sortBy = 'createdAt',
  sortOrder = 'DESC',
  page = 0,
  pageSize = 10,
  sortById,
  nestedFields = ['useCount', 'sales'],
  dateFields = ['createdAt', 'startDate', 'endDate'],
  getNestedValue,
}: SortAndPaginateOptions<T>) {
  const isSortByNested = nestedFields.some((field) => sortBy.includes(field));
  const key = isSortByNested ? `dealTicketStats.${sortBy}` : sortBy;
  const isDate = dateFields.includes(key);
  const baseValue = sortOrder === 'ASC' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;

  const sorted = [...data].sort((a, b) => {
    if (sortById) return (a as any).id === sortById ? -1 : 1;

    const aRaw = getNestedValue(a, key);
    const bRaw = getNestedValue(b, key);

    const parseValue = (value: any) => {
      if (isDate) return new Date(value).getTime();
      if (isSortByNested) return Number(value);
      return value ?? baseValue;
    };

    const aValue = parseValue(aRaw);
    const bValue = parseValue(bRaw);

    if (aValue > bValue) return sortOrder === 'ASC' ? 1 : -1;
    if (aValue < bValue) return sortOrder === 'ASC' ? -1 : 1;
    return 0;
  });

  const start = page;
  const end = start + pageSize;
  const paginated = sorted.slice(start, end);
  const total = data.length;

  return { data: paginated, total };
}

export const parseAndRoundHalfEven = (value: number | string | null | undefined, decimalPlaces = 7): number => {
  if (value === null || value === undefined) {
    return 0.0;
  }

  return new Decimal(value).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_EVEN).toNumber();
};

export const convertToDateRange = (dateRangeString: DateRangeString): DateRange => {
  return {
    start: dayjs(dateRangeString.startDate).format('MM/DD/YYYY') || null,
    end: dayjs(dateRangeString.endDate).format('MM/DD/YYYY') || null,
  };
};

export const getFullDays = (timeRanges: Schedules[] | null) => {
  return timeRanges
    ?.filter((range: any) => range.startTime === '00:00:00' && range.endTime === '23:59:59')
    .map((range: any) => range.day);
};

export const handleAllDayFuncationality = (playlists: TvPlaylist[]) => {
  return playlists.map((playlist) => {
    const allDay = getFullDays(playlist.schedules);
    return {
      ...playlist,
      items: playlist.items.map((item) => {
        return {
          ...item,
          template: item.template
            ? {
                ...item.template,
                content: item.template?.content?.map((menu: any) => ({
                  ...menu,
                  schedules: menu?.schedules?.map((schedule: any) => {
                    // Check if the schedule's day is in `allDay` and update it
                    if (allDay?.includes(schedule?.day)) {
                      return {
                        ...schedule,
                        startTime: '00:00:00',
                        endTime: '23:59:59',
                      };
                    }
                    return schedule; // Return original schedule if day not in `allDay`
                  }),
                  categories: menu.categories.map((category: any) => ({
                    ...category,
                    schedules: category?.schedules?.map((schedule: any) => {
                      // Check if the schedule's day is in `allDay` and update it
                      if (allDay?.includes(schedule?.day)) {
                        return {
                          ...schedule,
                          startTime: '00:00:00',
                          endTime: '23:59:59',
                        };
                      }
                      return schedule; // Return original schedule if day not in `allDay`
                    }),
                    items: category.items?.map((categoryItem: any) => ({
                      ...categoryItem,
                      schedules: categoryItem?.schedules?.map((schedule: any) => {
                        // Check if the schedule's day is in `allDay` and update it
                        if (allDay?.includes(schedule?.day)) {
                          return {
                            ...schedule,
                            startTime: '00:00:00',
                            endTime: '23:59:59',
                          };
                        }
                        return schedule; // Return original schedule if day not in `allDay`
                      }),
                    })),
                  })),
                })),
              }
            : null,
        };
      }),
    };
  });
};

export const createMappings = (modes: RestaurantMode[], code: string): Record<RestaurantMode, string> => {
  const result = {} as Record<RestaurantMode, string>;
  modes.forEach((mode) => {
    result[mode] = code;
  });
  return result;
};

// This function is specially for validatin gift card number
export const isValidGiftCardNumber = (cardNumber: string): boolean => {
  // Gift card number should be 19 alphanumeric characters
  const giftCardRegex = /^[A-Za-z0-9]{19}$/;
  return giftCardRegex.test(cardNumber);
};

// This function is to validate US numbers based on different format number
// US phone number regex: matches (xxx) xxx-xxxx, xxx-xxx-xxxx, and similar formats
export const isValidPhoneNumber = (phone: string): boolean => {
  // US phone number regex: matches (xxx) xxx-xxxx, xxx-xxx-xxxx, and similar formats
  const phoneRegex = /^(?:\+1)?(?:\d{3}[-.\s]?)?\d{3}[-.\s]?\d{4}$/;
  return phoneRegex.test(phone);
};

export const formatAmount = (value: number): string => {
  if (value === null || value === undefined) return '';
  const absValue = Math.abs(value);
  const formatted = absValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  return value < 0 ? `($${formatted})` : `$${formatted}`;
};

export const maskGiftCardNumber = (cardNumber: string) => {
  // pattern **** **** **** *** 1234
  // Ensure the card number is at least 4 characters long
  if (cardNumber?.length < 4) {
    return cardNumber; // Return the card number as it is if it's less than 4 digits
  }

  // Mask all but the last 4 digits with '*'
  const lastFourDigits = cardNumber?.slice(-4);
  const maskedPart = cardNumber?.slice(0, -4)?.replace(/./g, '*'); // Replace all but the last 4 digits with '*'

  // Insert spaces max of 4 or whatever remaining characters for readability
  const spacedMaskedPart = maskedPart.replace(/(.{4})/g, '$1 ');
  return spacedMaskedPart.trim() + lastFourDigits;
};

export const isValidUSNumber = (phone: string): boolean => {
  if (!phone) return false;
  const raw = String(phone).trim();

  // Allow only: +16381234567, 16381234567, 6381234567
  const formatRegex = /^(\+1|1)?\d{10}$/;
  if (!formatRegex.test(raw)) return false;

  let number = raw.replace(/\D/g, '');

  if (number.length === 11 && number.startsWith('1')) {
    number = number.slice(1);
  }

  if (number.length !== 10) return false;
  if (['0', '1'].includes(number[0])) return false;

  return true;
};

export function getDateRange(filter: DateRangeFilter, customStart?: string, customEnd?: string) {
  const now = dayjs();

  switch (filter) {
    case DateRangeFilter.TODAY: {
      const currentStart = now.startOf('day');
      const currentEnd = now.endOf('day');
      const prevStart = currentStart.subtract(1, 'day');
      const prevEnd = currentEnd.subtract(1, 'day');
      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: prevStart, end: prevEnd },
      };
    }

    case DateRangeFilter.LAST_7_DAYS: {
      const currentStart = now.subtract(6, 'day').startOf('day');
      const currentEnd = now.endOf('day');
      const prevStart = currentStart.subtract(7, 'day');
      const prevEnd = currentStart.subtract(1, 'day').endOf('day');
      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: prevStart, end: prevEnd },
      };
    }

    case DateRangeFilter.LAST_MONTH: {
      const currentStart = now.subtract(29, 'day').startOf('day');
      const currentEnd = now.endOf('day');
      const prevStart = currentStart.subtract(30, 'day');
      const prevEnd = currentStart.subtract(1, 'day').endOf('day');
      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: prevStart, end: prevEnd },
      };
    }

    case DateRangeFilter.LAST_YEAR: {
      const currentStart = now.subtract(364, 'day').startOf('day');
      const currentEnd = now.endOf('day');
      const prevStart = currentStart.subtract(365, 'day');
      const prevEnd = currentStart.subtract(1, 'day').endOf('day');
      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: prevStart, end: prevEnd },
      };
    }

    case DateRangeFilter.CUSTOM: {
      if (!customStart || !customEnd) throw new Error('Custom start and end dates are required');
      const currentStart = dayjs(customStart);
      const currentEnd = dayjs(customEnd);

      const diff = currentEnd.diff(currentStart, 'day') + 1; // how many days in custom range
      const prevStart = currentStart.subtract(diff, 'day');
      const prevEnd = currentStart.subtract(1, 'day').endOf('day');

      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: prevStart, end: prevEnd },
      };
    }

    case DateRangeFilter.ALL_TIME:
    default:
      return {
        current: { start: dayjs('1970-01-01'), end: now.endOf('day') },
        previous: { start: null, end: null }, // no previous for all time
      };
  }
}

export const getValidCustomers = (customers: any[]): ClassifiedCustomers => {
  const valid: any[] = [];
  const invalid: (any & { error: CustomerIssue })[] = [];
  const duplicates: (any & { error: CustomerIssue })[] = [];

  // Track first occurrence for each identity key
  const seen = new Map<string, { index: number; customer: any }>();

  customers.forEach((rawCustomer, index) => {
    const rawPhone = rawCustomer?.phoneNumber != null ? String(rawCustomer.phoneNumber) : '';
    const rawEmail = rawCustomer?.email != null ? String(rawCustomer.email) : '';

    const normalizedPhone = getPhoneNumber(rawPhone.trim().length ? rawPhone : null); // your function
    const normalizedEmail = rawEmail.trim().toLowerCase();

    const hasPhone = !!normalizedPhone;
    const hasEmail = normalizedEmail.length > 0;

    // 1) invalid: neither phone nor email
    if (!hasPhone && !hasEmail) {
      invalid.push({
        ...rawCustomer,
        message: 'Both phone number and email are missing.',
      });
      return;
    }

    // 2) compute dedupe key (prefer phone)
    const key = hasPhone ? `phone:${normalizedPhone}` : `email:${normalizedEmail}`;

    // 3) duplicate
    const existing = seen.get(key);
    if (existing) {
      duplicates.push({
        ...rawCustomer,
        message: `Duplicate row (same ${hasPhone ? 'phone' : 'email'} as an earlier row).`,
      });
      return;
    }

    // 4) first unique occurrence => valid
    const customer = {
      ...rawCustomer,
      phoneNumber: normalizedPhone ?? rawCustomer.phoneNumber,
      // keep original email field as you did; optionally also store normalizedEmail if useful
    };

    seen.set(key, { index, customer });
    valid.push(customer);
  });

  return {
    valid,
    invalid,
    duplicates,
    counts: {
      total: customers.length,
      valid: valid.length,
      invalid: invalid.length,
      duplicates: duplicates.length,
    },
  };
};

export const calculatePreviousPeriodDates = (startDate: Date, endDate: Date) => {
  const diffDays = differenceInDays(endDate, startDate);
  // const prevEndDate = subDays(startDate, 1); // This moves 1 day before the new range starts.
  const prevStartDate = subDays(startDate, diffDays);
  return {
    prevStartDate: prevStartDate,
    prevEndDate: startDate,
  };
};

export const normalizeDateRangeToClosoutTime = (
  sDate: string | Date,
  eDate: string | Date,
  closeOutTime: string,
  timezone?: string
) => {
  const startDate = new Date(sDate),
    endDate = addDays(new Date(eDate), 1);

  if (startDate && endDate && closeOutTime && isValidTime(closeOutTime)) {
    const [hours, minutes, seconds] = closeOutTime.split(':').map(Number);
    [startDate, endDate].forEach((time) => {
      time.setHours(hours, minutes, seconds);
    });
  }
  const covertToRestaurantTimeZone = moment(startDate).tz(timezone ?? 'America/Los_Angeles');
  let datesToReturn;
  const localDay = covertToRestaurantTimeZone.format('YYYY-MM-DD');
  const utcDay = moment(startDate).format('YYYY-MM-DD');
  if (localDay < utcDay) {
    datesToReturn = { startDate: addDays(startDate, 1), endDate: addDays(endDate, 1) };
  } else datesToReturn = { startDate, endDate };
  return datesToReturn;
};

export const normalizeDateToClosoutTime = (sDate: string | Date, closeOutTime: string, timezone?: string) => {
  const startDate = new Date(sDate);

  if (startDate && closeOutTime && isValidTime(closeOutTime)) {
    const [hours, minutes, seconds] = closeOutTime.split(':').map(Number);
    startDate.setHours(hours, minutes, seconds);
  }

  const covertToRestaurantTimeZone = moment(startDate).tz(timezone ?? 'America/Los_Angeles');
  let datesToReturn;
  const localDay = covertToRestaurantTimeZone.format('YYYY-MM-DD');
  const utcDay = moment(startDate).format('YYYY-MM-DD');
  if (localDay < utcDay) {
    datesToReturn = addDays(startDate, 1);
  } else datesToReturn = startDate;
  return datesToReturn;
};

export const calculatePercentageChange = (current: number, previous: number) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  const result = ((current - previous) / previous) * 100;
  // Round to 2 decimal places to avoid floating point precision issues
  return Number(result.toFixed(2));
};

// utils/base64-to-file.ts
export function dataUrlToFile(dataUrl: string, filename: string) {
  const matches = dataUrl.match(/^data:(.+);base64,(.+)$/);

  if (!matches || matches.length !== 3) {
    throw new Error('Invalid data URL');
  }

  const mimetype = matches[1];
  const base64Data = matches[2];
  const buffer = Buffer.from(base64Data, 'base64');

  // This mimics what Multer gives you
  return {
    buffer,
    mimetype,
    originalname: filename,
    size: buffer.length,
  };
}

export const getPhoneNumber = (phoneNumber: string | null) => {
  if (!phoneNumber) return null;

  // Remove spaces, parentheses, hyphens, dots, etc.
  const number = String(phoneNumber)?.trim().replace(/\D/g, '');

  // If less than 10 digits, invalid
  if (number.length < 10) {
    return null;
  }

  // If exactly 10 digits -> assume US number, prepend +1
  if (number.length === 10) {
    return `+1${number}`;
  }

  // If 11 digits and starts with 1 -> US number with country code
  if (number.length === 11 && number.startsWith('1')) {
    return `+${number}`;
  }

  // Otherwise treat as already complete international number
  return null;
};

export const IMPORT_PLATFORM_REQUIRED_COLUMNS: Record<IMPORT_GUESTS_PLATFORMS, string[]> = {
  [IMPORT_GUESTS_PLATFORMS.FIVE_STAR]: ['email', 'phone'],
  [IMPORT_GUESTS_PLATFORMS.TOAST]: ['email1', 'phone1'],
  [IMPORT_GUESTS_PLATFORMS.SPOT_HOPPER]: ['Phone', 'Email'],
  [IMPORT_GUESTS_PLATFORMS.POP_MENU]: ['phone', 'email'],
  [IMPORT_GUESTS_PLATFORMS.VENU_MANAGER]: ['Phone', 'Email'],
  [IMPORT_GUESTS_PLATFORMS.SQUARE]: ['Email Address', 'Phone Number'],
  [IMPORT_GUESTS_PLATFORMS.CLOVER]: ['Email Address', 'Phone Number'],
};

/**
 * Splits a full name into first and last name components with validation and cleaning.
 *
 * Validation Rules:
 * - Rejects firstName/lastName if purely numeric (e.g., "123", "5")
 * - Rejects firstName/lastName if it's a valid email address
 * - Returns empty string for invalid parts, preserving valid parts separately
 *
 * Cleaning Rules:
 * - Removes symbols: @, #, %, /, &, ?, _, °, etc.
 * - Preserves: letters (ASCII & accented), numbers, apostrophes ('), hyphens (-)
 * - Preserves accented characters used in international names (é, ã, ñ, á, etc.)
 * - Examples: "Luz VillaseÃ±or" → "Luz" + "VillaseÃ±or", "ãngel ãngel" → "ãngel" + "ãngel"
 *
 * Test Cases:
 * splitName('Cynthia lindaharris8645@yahoo.com') → { firstName: 'Cynthia', lastName: '' }
 * splitName('Mary 6') → { firstName: 'Mary', lastName: '' }
 * splitName('5 White') → { firstName: '', lastName: 'White' }
 * splitName('ãngel ãngel') → { firstName: 'ãngel', lastName: 'ãngel' }
 * splitName('Luz VillaseÃ±or') → { firstName: 'Luz', lastName: 'VillaseÃ±or' }
 * splitName('De LeÃ¡n-CedeÃ±o') → { firstName: 'De', lastName: 'LeÃ¡n-CedeÃ±o' }
 * splitName('MarÃ­a Bendet') → { firstName: 'MarÃ­a', lastName: 'Bendet' }
 * splitName('Cathe & Tom Cordova') → { firstName: 'Cathe', lastName: 'Tom Cordova' }
 * splitName('Walker?') → { firstName: 'Walker', lastName: '' }
 * splitName('John Doe') → { firstName: 'John', lastName: 'Doe' }
 * splitName('12345') → { firstName: '', lastName: '' }
 * splitName('test@example.com') → { firstName: '', lastName: '' }
 */
export const splitName = (fullName?: string) => {
  const empty = { firstName: '', lastName: '' };
  if (!fullName) return empty;

  const trimmed = fullName.trim();
  if (!trimmed) return empty;

  // Split by whitespace and filter out empty parts
  const parts = trimmed.split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return empty;

  // firstName is first word, lastName is remaining words joined with space
  let firstName = parts[0];
  let lastName = parts.slice(1).join(' ');

  // Validate and clean firstName
  // Reject if purely numeric (e.g., "5", "123") or valid email (e.g., "john@example.com")
  if (/^\d+$/.test(firstName) || isValidEmail(firstName)) {
    firstName = '';
  } else {
    // Remove symbols (@, #, %, /, &, ?, _, °, etc.) but preserve:
    // - Letters (including accented: À-ÿ, Ā-ſ for extended Unicode)
    // - Numbers, apostrophes, hyphens (common in names)
    // e.g., "Walker?" → "Walker", "Luz VillaseÃ±or" → "Luz VillaseÃ±or", "John@Doe" → "JohnDoe"
    firstName = firstName.replace(/[^a-zA-Z0-9À-ÿ\s'-]/g, '').trim();
  }

  // Validate and clean lastName (same logic as firstName)
  if (/^\d+$/.test(lastName) || isValidEmail(lastName)) {
    lastName = '';
  } else {
    // Remove symbols but preserve letters (ASCII & accented), numbers, apostrophes, hyphens
    // e.g., "Garcia#Lopez" → "GarciaLopez", "LeÃ¡n-CedeÃ±o" → "LeÃ¡n-CedeÃ±o", "Cathe & Tom" → "Cathe  Tom"
    lastName = lastName.replace(/[^a-zA-Z0-9À-ÿ\s'-]/g, '').trim();
  }

  return { firstName, lastName };
};

const stripTimePart = (value: string) => {
  return value
    .trim()
    .replace(/[T\s]+\d{1,2}:\d{2}(:\d{2})?(\.\d+)?(\s?[APMapm]{2})?$/, '')
    .trim();
};

/* eslint-disable */
const tryParseNumericDate = (value: string) => {
  const match = value.match(/^(\d{1,4})[\/.-](\d{1,2})[\/.-](\d{1,4})$/);
  if (!match) return null;

  const [, a, b, c] = match;

  // YYYY-MM-DD / YYYY/MM/DD — year-first is not US format, reject
  if (a.length === 4) {
    return null;
  }

  // MM/DD/YYYY or DD/MM/YYYY
  if (c.length === 4) {
    const n1 = Number(a);

    // n1 > 12 means the first segment can only be a day (DD/MM/YYYY) — not US format, reject
    if (n1 > 12) {
      return null;
    }

    // n2 > 12 means the second segment can only be a day (MM/DD/YYYY) — US format, accept
    // ambiguous case (both <= 12): assume US format (MM/DD/YYYY)
    const d = dayjs(`${a}/${b}/${c}`, 'M/D/YYYY', true);
    return d.isValid() ? d : null;
  }

  return null;
};

export const parseBirthday = (raw?: string | number | Date): string | null => {
  if (raw === null || raw === undefined) return null;

  // 1) Excel serial number
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;

    const excelEpoch = dayjs('1899-12-30');
    const d = excelEpoch.add(Math.floor(raw), 'day');

    return d.isValid() ? d.format(US_FORMAT) : null;
  }

  // 2) JS Date object
  if (raw instanceof Date) {
    const d = dayjs(raw);
    return d.isValid() ? d.format(US_FORMAT) : null;
  }

  // 3) String input
  const originalValue = String(raw).trim();
  if (!originalValue) return null;

  // Handle numeric string Excel serial like "45712"
  if (/^\d+(\.\d+)?$/.test(originalValue)) {
    const serial = Number(originalValue);
    if (Number.isFinite(serial)) {
      const excelEpoch = dayjs('1899-12-30');
      const d = excelEpoch.add(Math.floor(serial), 'day');
      return d.isValid() ? d.format(US_FORMAT) : null;
    }
  }

  const value = stripTimePart(originalValue);

  // First handle flexible numeric dates like:
  // 28/08/2026, 4/7/2026, 2026-03-10
  const numericDate = tryParseNumericDate(value);
  if (numericDate?.isValid()) {
    return numericDate.format(US_FORMAT);
  }

  // Then try known explicit formats
  for (const fmt of DATE_FORMATS) {
    const d = dayjs(value, fmt, true);
    if (d.isValid()) {
      return d.format(US_FORMAT);
    }
  }

  return null;
};

export const getTicketNumberSequenceName = (schemaName: string, restaurantId: number): string => {
  return `${schemaName}.ticket_number_seq_restaurant_${restaurantId}`;
};

export const sanitizeEmail = (value: unknown): string => {
  if (value == null) return '';
  const email = String(value).trim().toLowerCase();
  if (!email || email === 'null' || email === 'undefined') return '';
  return isValidEmail(email) ? email : '';
};

export const sanitizePhone = (value: unknown): string | null => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw || raw === 'null' || raw === 'undefined') return null;
  const phone = getPhoneNumber(raw);
  return phone && isValidUSNumber(phone) ? phone : null;
};

export const toStr = (value: unknown): string => {
  if (value == null) return '';
  const str = String(value).trim();
  return str === 'null' || str === 'undefined' ? '' : str;
};

export const toFiniteNumber = (value: unknown, fallback: number, opts: { positiveOnly?: boolean } = {}): number => {
  if (value == null || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return fallback;
  return opts.positiveOnly && n <= 0 ? fallback : n;
};

export const parseDateOrNow = (value: unknown): string => {
  const str = toStr(value);
  const d = str ? dayjs(str) : null;
  return d?.isValid() ? d.toISOString() : dayjs().toISOString();
};
